import * as React from 'react';
import { CustomProvider, DatePicker } from 'rsuite';
import 'rsuite/dist/rsuite-no-reset.min.css';
import './style.css';
import { showWarningSnackbar } from '../../../snackBar/snakbar';

export type AsOnDatePickerProps = {
  value?: Date | null;
  onConfirm: (date: Date) => void;
  onCancel?: () => void;
  shouldDisableDate?: (date: Date) => boolean;
  format?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
};

const parsePickerValue = (date: Date | null | undefined): Date | null => {
  if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return date;
};

const toPickerKey = (date: Date | null) => (date ? String(date.getTime()) : 'empty');

/** Strict dd/MM/yyyy parse from visible input (avoids rsuite coercing 31 → 01). */
const parseDdMmYyyyInput = (text: string): Date | null => {
  const trimmed = text.trim();
  const match = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return null;

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
};

export const AsOnDatePicker = ({
  value = null,
  onConfirm,
  onCancel,
  shouldDisableDate,
  format = 'dd/MM/yyyy',
  placeholder = 'DD/MM/YYYY',
  disabled = false,
  className = '',
}: AsOnDatePickerProps) => {
  const containerRef = React.useRef<HTMLSpanElement>(null);
  const isOpenRef = React.useRef(false);
  const committedViaOkRef = React.useRef(false);
  const [preventClose, setPreventClose] = React.useState(false);
  const lastCommittedRef = React.useRef<Date | null>(parsePickerValue(value));
  const draftRef = React.useRef<Date | null>(parsePickerValue(value));
  const [pickerValue, setPickerValue] = React.useState<Date | null>(() => parsePickerValue(value));
  const [pickerKey, setPickerKey] = React.useState(() => toPickerKey(parsePickerValue(value)));

  const readInputDate = React.useCallback((): Date | null => {
    const input = containerRef.current?.querySelector<HTMLInputElement>('input');
    if (!input?.value) return null;
    return parseDdMmYyyyInput(input.value);
  }, []);

  const syncDraftToInput = React.useCallback((next: Date | null | undefined) => {
    const parsed = parsePickerValue(next);
    if (!parsed) return;
    draftRef.current = parsed;
    setPickerValue(parsed);
  }, []);

  // Sync from parent only when picker is closed
  React.useEffect(() => {
    if (isOpenRef.current) return;
    const parsed = parsePickerValue(value);
    lastCommittedRef.current = parsed;
    draftRef.current = parsed;
    setPickerValue(parsed);
    setPickerKey(toPickerKey(parsed));
  }, [value]);

  const validateDate = React.useCallback(
    (date: Date | null): { isValid: boolean; errorMessage?: string } => {
      if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) {
        return { isValid: false, errorMessage: 'Please enter a valid date (DD/MM/YYYY)' };
      }
      if (shouldDisableDate?.(date)) {
        return { isValid: false, errorMessage: 'Selected date is outside the allowed range' };
      }
      return { isValid: true };
    },
    [shouldDisableDate]
  );

  const commitDate = React.useCallback(
    (date: Date | null) => {
      const validation = validateDate(date);
      if (!validation.isValid) {
        showWarningSnackbar(validation.errorMessage || 'Invalid date');
        setPreventClose(true);
        return false;
      }
      lastCommittedRef.current = date;
      draftRef.current = date;
      setPickerValue(date);
      setPickerKey(toPickerKey(date));
      committedViaOkRef.current = true;
      onConfirm(date as Date);
      return true;
    },
    [onConfirm, validateDate]
  );

  const handleOk = React.useCallback(
    (next: Date) => {
      const fromPicker = parsePickerValue(next) ?? draftRef.current ?? pickerValue;
      commitDate(fromPicker);
    },
    [commitDate, pickerValue]
  );

  const revertDraft = React.useCallback(() => {
    const committed = parsePickerValue(lastCommittedRef.current);
    draftRef.current = committed;
    setPickerValue(committed);
    setPickerKey(toPickerKey(committed));
  }, []);

  const handleClose = React.useCallback(() => {
    if (preventClose) {
      setPreventClose(false);
      return;
    }
    if (committedViaOkRef.current) {
      committedViaOkRef.current = false;
      isOpenRef.current = false;
      return;
    }
    revertDraft();
    isOpenRef.current = false;
    onCancel?.();
  }, [preventClose, revertDraft, onCancel]);

  const handleOpen = React.useCallback(() => {
    isOpenRef.current = true;
    setPreventClose(false);
    committedViaOkRef.current = false;
    const committed = parsePickerValue(lastCommittedRef.current);
    draftRef.current = committed;
    setPickerValue(committed);
  }, []);

  // Enter confirms typed date (calendar uses its own OK button)
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter') return;
      const container = containerRef.current;
      if (!container?.contains(document.activeElement)) return;

      event.preventDefault();
      event.stopPropagation();
      const typed = readInputDate();
      if (typed) {
        syncDraftToInput(typed);
      }
      commitDate(typed ?? draftRef.current ?? pickerValue);
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [commitDate, readInputDate, syncDraftToInput, pickerValue]);

  return (
    <span
      ref={containerRef}
      className="rs-stack as-on-date-picker-field"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <CustomProvider theme="light">
        <DatePicker
          key={pickerKey}
          value={pickerValue}
          format={format}
          placeholder={placeholder}
          editable
          oneTap={false}
          cleanable
          disabled={disabled}
          onOpen={handleOpen}
          onClose={handleClose}
          onChange={(next) => syncDraftToInput(next)}
          onSelect={(next) => syncDraftToInput(next)}
          onOk={handleOk}
          shouldDisableDate={shouldDisableDate}
          placement="autoVerticalStart"
          preventOverflow
          className={`mui-like-rsuite as-on-date-picker-toggle ${className}`.trim()}
          menuClassName="as-on-date-picker-panel"
          menuStyle={{ width: 'auto' }}
        />
      </CustomProvider>
    </span>
  );
};
